/*
  Material tab spreadsheets (UI overhaul wave 1, screen 05 idea 1 / 7):
  - readBoqSpreadsheet(file)  → [{ part_number, description, qty }] from .xlsx (first sheet) / .csv / .tsv / .txt
  - boqRowsToPasteText(rows)   → tab-separated text for the Add BOQ Item paste importer
  - boqTemplateXlsx()          → Uint8Array of a one-sheet template (Part No, Description, BOQ Qty)
  - materialRowsToXlsx(rows)   → Uint8Array export of the material table
  - parseQuantity(value)       → number; '1,200' is 1200 (thousands), '1,5' is 1.5 (decimal comma)
  No dependencies beyond fflate (already used for shop-drawing spreadsheets).
*/
import { strToU8, unzipSync, zipSync } from 'fflate';

const readAsArrayBuffer = (file) => (
  typeof file.arrayBuffer === 'function'
    ? file.arrayBuffer()
    : new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
      reader.readAsArrayBuffer(file);
    })
);

const readAsText = (file) => (
  typeof file.text === 'function'
    ? file.text()
    : new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
      reader.readAsText(file);
    })
);

const parseXml = (xml) => new DOMParser().parseFromString(xml, 'application/xml');
const localName = (node) => node?.localName || String(node?.nodeName || '').split(':').pop();
const byName = (root, name) => [...(root?.getElementsByTagName('*') || [])].filter((node) => localName(node) === name);
const attr = (node, name) => node?.getAttribute(name) || '';
const entryText = (entries, path) => (entries[path] ? new TextDecoder('utf-8').decode(entries[path]) : '');

const columnIndex = (ref) => {
  const letters = String(ref || '').match(/^[A-Z]+/i)?.[0] || '';
  return letters.toUpperCase().split('').reduce((total, char) => (total * 26) + char.charCodeAt(0) - 64, 0) - 1;
};

const firstSheetPath = (entries) => {
  const workbook = parseXml(entryText(entries, 'xl/workbook.xml'));
  const rels = parseXml(entryText(entries, 'xl/_rels/workbook.xml.rels'));
  const sheet = byName(workbook, 'sheet')[0];
  if (!sheet) throw new Error('The workbook has no sheets');
  const relId = attr(sheet, 'r:id') || attr(sheet, 'id');
  const target = byName(rels, 'Relationship').find((rel) => attr(rel, 'Id') === relId);
  const path = attr(target, 'Target') || 'worksheets/sheet1.xml';
  return path.startsWith('xl/') ? path : `xl/${path.replace(/^\/+/, '')}`;
};

const readXlsxRows = (buffer) => {
  const entries = unzipSync(new Uint8Array(buffer));
  if (!entries['xl/workbook.xml']) throw new Error('This is not an Excel (.xlsx) workbook');
  const shared = byName(parseXml(entryText(entries, 'xl/sharedStrings.xml') || '<sst/>'), 'si')
    .map((item) => byName(item, 't').map((node) => node.textContent || '').join(''));
  const sheet = parseXml(entryText(entries, firstSheetPath(entries)));
  return byName(sheet, 'row').map((row) => {
    const values = [];
    byName(row, 'c').forEach((cell, fallback) => {
      const index = Math.max(columnIndex(attr(cell, 'r')), fallback);
      const type = attr(cell, 't');
      let value;
      if (type === 'inlineStr') value = byName(cell, 't').map((node) => node.textContent || '').join('');
      else {
        const raw = byName(cell, 'v')[0]?.textContent || '';
        value = type === 's' ? (shared[Number(raw)] || '') : raw;
      }
      values[index] = value;
    });
    return Array.from(values, (value) => String(value ?? '').trim());
  });
};

const splitDelimited = (line, separator) => {
  const cells = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"' && quoted && line[index + 1] === '"') { current += '"'; index += 1; }
    else if (char === '"') quoted = !quoted;
    else if (char === separator && !quoted) { cells.push(current.trim()); current = ''; }
    else current += char;
  }
  cells.push(current.trim());
  return cells;
};

const readDelimitedRows = (textValue) => {
  const lines = String(textValue || '').split(/\r?\n/).filter((line) => line.trim());
  if (!lines.length) return [];
  const separator = lines[0].includes('\t') ? '\t' : (lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',');
  return lines.map((line) => splitDelimited(line, separator));
};

/**
 * A quantity cell / pasted value → number (0 when empty or unreadable).
 * '1,200' and '1,200.5' are thousands ('1,200' = 1200, never 1.2); a comma that cannot be a thousands
 * separator is a decimal comma ('1,5' = 1.5, '12,25' = 12.25). Units and spaces are ignored ('12 pcs').
 */
export const parseQuantity = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const raw = String(value ?? '').trim();
  if (!raw) return 0;
  const negative = /^-/.test(raw);
  const digits = raw.replace(/[^\d.,]/g, '');
  let normalized;
  if (/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(digits)) normalized = digits.replace(/,/g, '');
  else if (digits.includes('.')) normalized = digits.replace(/,/g, '');
  else normalized = digits.replace(',', '.');
  const parsed = Number(normalized);
  if (!normalized || !Number.isFinite(parsed)) return 0;
  return negative ? -parsed : parsed;
};
const toQty = parseQuantity;

/** Finds Part No / Description / Qty columns from a header row; falls back to columns A, B, C. */
export function rowsToBoqRows(rows = []) {
  const clean = rows.filter((row) => row.some((cell) => String(cell || '').trim()));
  if (!clean.length) return [];
  const headerIndex = clean.slice(0, 5).findIndex((row) => row.some((cell) => /qty|quantity/i.test(cell))
    && row.some((cell) => /part|model|code|item|desc/i.test(cell)));
  let columns = { part: 0, description: 1, qty: 2 };
  let body = clean;
  if (headerIndex >= 0) {
    const header = clean[headerIndex].map((cell) => String(cell || '').toLowerCase());
    const find = (pattern, fallback) => {
      const index = header.findIndex((cell) => pattern.test(cell));
      return index >= 0 ? index : fallback;
    };
    columns = {
      part: find(/part|model|code|item\s*no|item\s*code/, 0),
      description: find(/desc/, 1),
      qty: find(/boq\s*qty|qty|quantity/, 2),
    };
    body = clean.slice(headerIndex + 1);
  }
  return body
    .map((row) => ({
      part_number: String(row[columns.part] || '').trim(),
      description: String(row[columns.description] || '').trim(),
      qty: toQty(row[columns.qty]),
    }))
    .filter((row) => row.qty > 0 && (row.part_number || row.description));
}

/** Reads a BOQ spreadsheet the user picked. Throws with a readable message on bad files. */
export async function readBoqSpreadsheet(file) {
  if (!file) return [];
  const extension = String(file.name || '').split('.').pop().toLowerCase();
  if (extension === 'xls') throw new Error('Old .xls files are not supported — save the sheet as .xlsx or .csv');
  const rows = extension === 'xlsx'
    ? readXlsxRows(await readAsArrayBuffer(file))
    : readDelimitedRows(await readAsText(file));
  return rowsToBoqRows(rows);
}

export const boqRowsToPasteText = (rows = []) => rows
  .map((row) => [row.part_number, row.description, row.qty].map((cell) => String(cell ?? '').replace(/[\t\r\n]+/g, ' ')).join('\t'))
  .join('\n');

/* ------------------------------------------------------------------ writing */

const escapeXml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

const columnName = (index) => {
  let name = '';
  let current = index + 1;
  while (current > 0) {
    current -= 1;
    name = String.fromCharCode(65 + (current % 26)) + name;
    current = Math.floor(current / 26);
  }
  return name;
};

const cellXml = (value, ref) => (
  typeof value === 'number' && Number.isFinite(value)
    ? `<c r="${ref}"><v>${value}</v></c>`
    : `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`
);

export function rowsToXlsx(rows, sheetName = 'Sheet1') {
  const sheetRows = rows.map((row, rowIndex) => (
    `<row r="${rowIndex + 1}">${row.map((value, colIndex) => cellXml(value, `${columnName(colIndex)}${rowIndex + 1}`)).join('')}</row>`
  )).join('');
  return zipSync({
    '[Content_Types].xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
      + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
      + '<Default Extension="xml" ContentType="application/xml"/>'
      + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
      + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
      + '</Types>'),
    '_rels/.rels': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
      + '</Relationships>'),
    'xl/workbook.xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
      + `<sheets><sheet name="${escapeXml(String(sheetName).slice(0, 31))}" sheetId="1" r:id="rId1"/></sheets>`
      + '</workbook>'),
    'xl/_rels/workbook.xml.rels': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
      + '</Relationships>'),
    'xl/worksheets/sheet1.xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
      + `<sheetData>${sheetRows}</sheetData>`
      + '</worksheet>'),
  });
}

export const BOQ_TEMPLATE_HEADERS = ['Part No', 'Description', 'BOQ Qty'];

export const boqTemplateXlsx = () => rowsToXlsx([
  BOQ_TEMPLATE_HEADERS,
  ['MTN6705-0008', 'SpaceLogic KNX Switch/Blind Master', 5],
], 'BOQ');

export const MATERIAL_EXPORT_HEADERS = [
  '#', 'Part No', 'Description', 'Sub BOQ', 'Unit', 'BOQ Qty', 'Variations', 'Delivered', 'Progress %', 'Remaining', 'Site needs', 'VO needed',
];

/** rows: page material rows (with totals from the page's materialRows) + progress helper output. */
export const materialRowsToXlsx = (rows = [], getProgress) => rowsToXlsx([
  MATERIAL_EXPORT_HEADERS,
  ...rows.map((row, index) => [
    index + 1,
    row.part_number || '',
    row.description || '',
    row.sub_boq || '',
    row.unit || 'pcs',
    Number(row.boq_qty || 0),
    Number(row.variationTotal || 0),
    Number(row.deliveredTotal || 0),
    getProgress ? getProgress(row).percent : '',
    Number(row.remaining || 0),
    Number(row.siteNeedsQty || 0),
    Math.max(0, Number(row.variationNeeded || 0)),
  ]),
], 'Material');
